<template>
  <button
    :class="[
      $style.button,
      {
        [$style.typeDefault]: type === 'default',
        [$style.typeGhost]: type === 'ghost',
        [$style.selected]: selected,
      },
    ]"
    type="button"
    :disabled="disabled"
  >
    <div :class="$style.container">
      <SCASvgIcon v-if="icon" size="20px" :class="$style.icon" :icon="icon" />
      <div v-if="slots['default']" :class="$style.lblContainer">
        <span :class="$style.lblText" :data-text="label"
          ><span :class="$style.lblTextInner"><slot /></span
        ></span>
        <div v-if="dot" :class="$style.dotContainer">
          <SCASvgIcon :class="$style.dotIcon" size="12px" icon="generic/dot" />
        </div>
      </div>
    </div>
  </button>
</template>

<script setup lang="ts">
import { useSlots } from "vue";

import SCASvgIcon from "./SCASvgIcon.vue";
import type { SCATabProps } from "./types";

withDefaults(defineProps<SCATabProps>(), {
  icon: undefined,
  selected: false,
  disabled: false,
  type: "default",
  dot: false,
  label: undefined,
});

const slots = useSlots();
</script>

<style module lang="scss">
@use "../styles/generated/figma_typo_mixins.scss" as FigmaTypoMixins;
@use "../styles/icon.scss" as IconMixins;

.icon {
  display: flex;
  padding: 2px 0px;

  align-items: flex-start;
  gap: 8px;
  @include IconMixins.icon-color(currentColor);
}

.dotIcon {
  @include IconMixins.icon-color(currentColor);
}

.dotContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 2px;
}

.lblContainer {
  display: flex;
}

.lblText {
  display: inline-flex;
  flex-direction: column;

  &::before {
    content: attr(data-text);
    font-weight: 600;
    height: 0;
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    white-space: nowrap;
  }
}

.lblTextInner {
  display: inline;
}

.container {
  white-space: nowrap;
  span {
    white-space: nowrap;
  }
}

.button {
  @include FigmaTypoMixins.typo-body-medium;
  color: var(--sca-tab-group-color, var(--color-action-neutral-normal));
  display: flex;
  justify-content: center;
  align-items: center;
  gap: var(--space-component-gap-small);

  &.typeDefault {
    height: 100%;
    border-bottom: 1px solid transparent;
    padding: var(--space-component-padding-small) var(--space-component-padding-null);
    margin-right: var(--space-component-padding-small);
    letter-spacing: normal;
    &:not([disabled]) {
      &:focus,
      &:hover {
        border-bottom: 1px solid var(--color-action-neutral-subtle-hover);
        outline: none;
      }
      &:active {
        color: var(--color-content-informative-primary);
        border-bottom: 1px solid var(--color-content-informative-primary);
      }
    }

    &.selected {
      border-bottom: 1px solid var(--color-content-informative-primary) !important;
      color: var(--color-content-informative-primary) !important;
      font-weight: 600;

      &:focus {
        border-bottom: 1px solid var(--color-content-informative-primary);
        color: var(--color-action-neutral-normal);
      }

      &:active {
        color: var(--color-action-primary-selected);
        background-color: var(--color-action-primary-subtle-selected);
      }
    }

    .container {
      display: flex;
      justify-content: center;
      align-items: center;
      gap: var(--space-component-gap-xSmall);
      border-radius: var(--border-radius-medium);
    }
  }

  &.typeGhost {
    padding: 7px var(--space-component-padding-small);
    color: var(--sca-tab-group-color, var(--color-content-primary));
    line-height: 14px;
    letter-spacing: 0.24px;
    background-color: var(--color-action-ghost-normal);
    border-radius: var(--border-radius-large);

    &:not([disabled]):not(.selected) {
      &:focus,
      &:hover {
        color: var(--color-content-primary);
        background-color: var(--color-background-neutral-subtle);
        outline: none;
      }
    }
    &.selected {
      color: var(--color-content-informative-primary);
      background-color: var(--color-background-informative-onSubtle);

      &:focus {
        outline: none;
      }

      &:disabled {
        color: var(--color-action-neutral-selected);
        background-color: var(--color-action-ghost-selected);
      }
    }
  }

  &:disabled {
    opacity: 0.5;
  }
}
</style>
